import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Loader2, Save, ExternalLink, Sparkles, Brain, Languages, TrendingUp, Video, PenTool, CheckCheck, Globe } from 'lucide-react';
import { toast } from "sonner";

interface AiModelsConfig {
  ai_model_lang_detection: string;
  ai_model_trend_analysis: string;
  ai_model_recommendations: string;
  ai_model_video_analysis: string;
  ai_model_text_generation: string;
  ai_model_grammar_check: string;
  ai_model_rephrase: string;
  ai_model_translation: string;
}

const MODEL_LABELS: Record<keyof AiModelsConfig, { label: string, description: string, icon: any }> = {
  ai_model_lang_detection: { 
    label: 'Language Detection', 
    description: 'Model used to detect the primary language of social accounts.',
    icon: Languages
  },
  ai_model_trend_analysis: { 
    label: 'Trend Analysis', 
    description: 'Model used to discover and group trends from monitored accounts.',
    icon: TrendingUp
  },
  ai_model_recommendations: { 
    label: 'Daily Missions', 
    description: 'Model used to generate personalized post recommendations.',
    icon: Brain
  },
  ai_model_video_analysis: { 
    label: 'Video Analysis', 
    description: 'Model used to analyze video content and generate captions.',
    icon: Video
  },
  ai_model_text_generation: { 
    label: 'Text Assistant', 
    description: 'Model used for general post text assistance and translation.',
    icon: PenTool
  },
  ai_model_grammar_check: { 
    label: 'Grammar Check & Fix', 
    description: 'Model used to check and fix grammar and spelling in post text.',
    icon: CheckCheck
  },
  ai_model_rephrase: { 
    label: 'Text Rephrasing', 
    description: 'Model used to rephrase post text to make it more engaging.',
    icon: Sparkles
  },
  ai_model_translation: { 
    label: 'Post Translation', 
    description: 'Model used to translate posts to multiple languages automatically.',
    icon: Globe
  }
};

export default function AiSettings() {
  const [models, setModels] = useState<AiModelsConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const token = localStorage.getItem('token');

  const fetchModels = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/admin/ai-models`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModels(response.data);
    } catch (error) {
      console.error('Error fetching AI models:', error);
      toast.error('Failed to load AI model configurations.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchModels();
  }, []);

  const handleUpdateModel = async (key: string, value: string) => {
    setSaving(key);
    try {
      await axios.put(`${API_BASE_URL}/admin/ai-models/${key}`, { value }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Model configuration updated.');
      setModels(prev => prev ? { ...prev, [key]: value } : null);
    } catch (error) {
      console.error('Error updating AI model:', error);
      toast.error('Failed to update AI model.');
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="bg-card border-border text-foreground shadow-xl overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-purple-500 to-primary w-full" />
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-400" />
              AI Engine Configuration
            </CardTitle>
            <a 
              href="https://platform.openai.com/docs/models" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-xs text-primary hover:text-primary/80 flex items-center gap-1 transition-colors"
            >
              OpenAI Models List
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
          <CardDescription className="text-muted-foreground">
            Define which OpenAI models should be used for different system features. 
            Type the model name exactly as it appears in the OpenAI documentation.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {models && Object.entries(MODEL_LABELS).map(([key, info]) => {
            const Icon = info.icon;
            const currentModel = (models as any)[key];

            return (
              <div key={key} className="p-4 bg-muted/50 rounded-xl border border-border space-y-3 transition-all hover:border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-card rounded-lg">
                      <Icon className="h-4 w-4 text-foreground" />
                    </div>
                    <div>
                      <Label className="text-sm font-bold text-foreground">{info.label}</Label>
                      <p className="text-[11px] text-muted-foreground">{info.description}</p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Input
                    defaultValue={currentModel}
                    className="bg-background border-border text-foreground h-9"
                    placeholder="e.g. gpt-4o-mini"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleUpdateModel(key, e.currentTarget.value);
                      }
                    }}
                    id={`input-${key}`}
                  />
                  <Button
                    size="sm"
                    disabled={saving === key}
                    onClick={() => {
                      const input = document.getElementById(`input-${key}`) as HTMLInputElement;
                      handleUpdateModel(key, input.value);
                    }}
                    className="bg-primary hover:bg-primary/90 h-9 px-4 shrink-0"
                  >
                    {saving === key ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            );
          })}

          <div className="bg-amber-500/10 border border-amber-500/20 p-4 rounded-xl">
            <p className="text-xs text-amber-200/80 leading-relaxed">
              <strong>Tip:</strong> You can use high-performance models like <code>gpt-4o</code> or <code>o1-preview</code> for complex tasks like video analysis or trend discovery, and cheaper ones like <code>gpt-4o-mini</code> for language detection.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
